import { ReactNode } from 'react';

import { Card, CardContent } from '@/components/ui/card';
import {
  CMSSystem,
  Firm,
  FirmStatus,
  FirmType,
  LeadSystem,
  MedRecsBrandingMode,
  TimeZone,
} from '@/services/types/firms-types';
import { getUserDisplayName } from '@/utils/user';

// Define the field config structure for uniform rendering

export type FirmFieldConfigV2 = Exclude<
  {
    [K in keyof Firm]: {
      name: K;
      label: string;
      required?: boolean;
      type?: 'text' | 'email' | 'number' | 'select' | 'textarea' | 'url';
      placeholder?: string;
      options?: { value: string; label: string }[];
      transformValue?: (value: Firm[K]) => string;
    };
  }[keyof Firm],
  undefined
>;

export const getFirmFieldsV2 = (): FirmFieldConfigV2[] => [
  // Required fields first
  {
    name: 'name',
    label: 'Firm Name',
    required: true,
    type: 'text',
    placeholder: 'Enter firm name',
  },
  {
    name: 'managing_paralegal',
    label: 'Managing Paralegal',
    required: true,
    type: 'select',
    placeholder: 'Select managing paralegal',
    transformValue: (value) => {
      if (!value) {
        return 'Not set';
      }
      return getUserDisplayName(value);
    },
  },
  // Basic info fields
  {
    name: 'full_name',
    label: 'Full Legal Name',
    type: 'text',
    placeholder: 'Enter full legal name',
  },
  {
    name: 'email',
    label: 'Email',
    type: 'email',
    placeholder: 'Enter firm email',
  },
  {
    name: 'adobe_sign_email',
    label: 'Adobe Sign Email',
    type: 'email',
    placeholder: 'Enter Adobe Sign email',
  },
  {
    name: 'filevine_org_id',
    label: 'Filevine Organization ID',
    type: 'number',
    placeholder: 'Enter Filevine organization ID',
  },
  // Status and classification
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    placeholder: 'Select status',
    options: Object.values(FirmStatus).map((value) => ({
      value,
      label: value,
    })),
  },
  {
    name: 'type',
    label: 'Type',
    type: 'select',
    placeholder: 'Select type',
    options: Object.values(FirmType).map((value) => ({ value, label: value })),
  },
  {
    name: 'medrecs_branding_mode',
    label: 'MedRecs Branding Mode',
    type: 'select',
    placeholder: 'Select branding mode',
    options: [
      { value: MedRecsBrandingMode.CUSTOM, label: 'Custom (Legacy)' },
      { value: MedRecsBrandingMode.FIRM, label: 'Firm Branding' },
      { value: MedRecsBrandingMode.FINCH, label: 'Finch Branding' },
    ],
  },
  {
    name: 'time_zone',
    label: 'Time Zone',
    type: 'select',
    placeholder: 'Select time zone',
    options: Object.values(TimeZone).map((value) => ({ value, label: value })),
  },
  {
    name: 'primary_state',
    label: 'Primary State',
    type: 'text',
    placeholder: 'Enter primary state',
  },
  // System integrations
  {
    name: 'cms_system',
    label: 'CMS System',
    type: 'select',
    placeholder: 'Select CMS system',
    options: Object.values(CMSSystem).map((value) => ({ value, label: value })),
  },
  {
    name: 'cms_link',
    label: 'CMS Link',
    type: 'url',
    placeholder: 'Enter CMS system URL',
  },
  {
    name: 'lead_system',
    label: 'Lead System',
    type: 'select',
    placeholder: 'Select lead system',
    options: Object.values(LeadSystem).map((value) => ({
      value,
      label: value,
    })),
  },
  {
    name: 'lead_system_link',
    label: 'Lead System Link',
    type: 'url',
    placeholder: 'Enter lead system URL',
  },
  // Notes and documentation (textarea fields at end)
  {
    name: 'case_preferences',
    label: 'Case Preferences',
    type: 'textarea',
    placeholder: 'Enter case preferences and guidelines',
  },
  {
    name: 'commentary',
    label: 'Commentary',
    type: 'textarea',
    placeholder: 'Enter operational commentary and updates',
  },
  {
    name: 'errors',
    label: 'Errors',
    type: 'textarea',
    placeholder: 'Document errors and issues',
  },
  {
    name: 'plan_to_derisk',
    label: 'Plan to Derisk',
    type: 'textarea',
    placeholder: 'Enter derisking plans and strategies',
  },
];
export interface FirmLayoutProps {
  children: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  inline?: boolean;
}

// A shared layout component to ensure consistent presentation
// Following "Enterprise Compact" design - high-density, no fat padding
export const FirmLayout: React.FC<FirmLayoutProps> = ({
  children,
  header,
  footer,
  inline = false,
}) => {
  // Grid layout for form fields - shared between inline and card modes
  const gridContent = (
    <dl className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-2">
      {children}
    </dl>
  );

  // Consistent footer styling for both modes
  const footerContent = footer ? (
    <div className="pt-4 border-t border-gray-200 dark:border-zinc-800">
      {footer}
    </div>
  ) : null;

  if (inline) {
    return (
      <div className="space-y-3">
        {header}
        {gridContent}
        {footerContent}
      </div>
    );
  }

  return (
    <Card className="border border-gray-200 dark:border-zinc-800">
      {header}
      <CardContent className="p-4">
        {gridContent}
        {footerContent}
      </CardContent>
    </Card>
  );
};

// A consistent field row layout to use in both view and edit modes
// Compact design: stacked label + input, minimal vertical spacing
export const FirmFieldRow: React.FC<{
  label: string;
  required?: boolean;
  children: ReactNode;
  fullWidth?: boolean;
}> = ({ label, required, children, fullWidth = false }) => {
  return (
    <div className={fullWidth ? 'col-span-1 lg:col-span-2' : ''}>
      <dt className="text-xs font-medium text-gray-600 dark:text-zinc-400 leading-tight mb-1">
        {label}
        {required && (
          <span className="text-destructive ml-1 font-normal">(Required)</span>
        )}
      </dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
};
